// Layouts/Assignments/parts/GradableSubmissionCard.tsx
import React from 'react';

const C = {
  navy: '#0e225d',
  navyMid: '#1a3370',
  gold: '#fbb21f',
  goldDim: 'rgba(251,178,31,0.14)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white: '#ffffff',
  border: '#e5e7eb',
  muted: '#9ca3af',
  sub: '#6b7280',
  body: '#1f2937',
  bg: '#F8FAFC',
};

interface SubmissionFile {
  file_name: string;
  key: string;
  url: string;
  size: number;
  mime_type: string;
  uploaded_at: string;
}

export interface Submission {
  submission_id: string;
  assignment_id: string;
  student_id: string;
  student_name: string;
  submission_text: string;
  files: SubmissionFile[];
  status: 'draft' | 'submitted' | 'graded';
  submission_date: string | null;
  is_late: boolean;
  marks_obtained: number | null;
  total_marks: number | null;
  feedback: string | null;
  reviewed_by: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  created_at: string;
  submitted_at: string | null;
}

interface Props {
  submission: Submission;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
}

const statusColor = (status: string) => {
  switch (status) {
    case 'draft': return C.sub;
    case 'submitted': return '#3b82f6';
    case 'graded': return '#10B981';
    default: return C.sub;
  }
};

const statusIcon = (status: string) => {
  switch (status) {
    case 'draft': return 'pi-pencil';
    case 'submitted': return 'pi-check-circle';
    case 'graded': return 'pi-star-fill';
    default: return 'pi-file';
  }
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return dateString; }
};

/**
 * Web port of components/assignments/teacher/GradableSubmissionCard.tsx (RN).
 * Same data shape and grading-state logic; checkbox + layout adapted for web.
 */
const GradableSubmissionCard: React.FC<Props> = ({ submission, isSelected, onToggleSelect }) => {
  const color = statusColor(submission.status);
  const selectable = submission.status !== 'graded';

  return (
    <div style={{
      position: 'relative',
      background: C.white,
      borderRadius: '14px',
      padding: '16px',
      border: `1px solid ${isSelected ? C.goldBorder : C.border}`,
      boxShadow: isSelected ? '0 2px 10px rgba(251,178,31,0.15)' : '0 1px 4px rgba(14,34,93,0.05)',
      transition: 'all 0.15s ease',
    }}>
      {selectable && (
        <button
          onClick={() => onToggleSelect(submission.submission_id)}
          title={isSelected ? 'Deselect' : 'Select for grading'}
          style={{
            position: 'absolute', top: '14px', right: '14px',
            width: '24px', height: '24px', borderRadius: '6px',
            border: `2px solid ${C.navy}`,
            background: isSelected ? C.navy : C.white,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', padding: 0,
          }}
        >
          {isSelected && <i className="pi pi-check" style={{ fontSize: '0.7rem', color: C.gold }} />}
        </button>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', paddingRight: selectable ? '36px' : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '20px', flexShrink: 0,
            background: C.navy, border: `2px solid ${C.gold}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ color: C.white, fontWeight: 700, fontSize: '0.95rem' }}>
              {submission.student_name?.charAt(0).toUpperCase() || 'S'}
            </span>
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: '0 0 3px 0', fontWeight: 700, color: C.navy, fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {submission.student_name || 'Unknown Student'}
            </p>
            <span style={{ fontSize: '0.75rem', color: C.sub }}>
              <i className="pi pi-clock" style={{ fontSize: '0.68rem', marginRight: '4px' }} />
              {formatDate(submission.submitted_at)}
            </span>
          </div>
        </div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          padding: '5px 10px', borderRadius: '8px',
          background: `${color}20`, flexShrink: 0,
        }}>
          <i className={`pi ${statusIcon(submission.status)}`} style={{ fontSize: '0.7rem', color }} />
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color }}>
            {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
          </span>
        </span>
      </div>

      {submission.is_late && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#FEF3C7', padding: '7px 10px', borderRadius: '8px', marginBottom: '10px' }}>
          <i className="pi pi-exclamation-circle" style={{ fontSize: '0.75rem', color: '#F59E0B' }} />
          <span style={{ fontSize: '0.76rem', fontWeight: 600, color: '#92400E' }}>Late Submission</span>
        </div>
      )}

      {isSelected && submission.status !== 'graded' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, padding: '8px 10px', borderRadius: '8px', marginBottom: '10px' }}>
          <i className="pi pi-check-circle" style={{ fontSize: '0.75rem', color: C.navy }} />
          <span style={{ fontSize: '0.74rem', fontWeight: 600, color: C.navy }}>
            Selected for grading — use “Grade” to enter marks
          </span>
        </div>
      )}

      {submission.submission_text && (
        <div style={{ background: C.bg, padding: '10px 12px', borderRadius: '9px', marginBottom: '10px', border: `1px solid ${C.border}` }}>
          <p style={{ margin: '0 0 4px 0', fontSize: '0.72rem', fontWeight: 700, color: C.navy }}>Answer:</p>
          <p style={{ margin: 0, fontSize: '0.82rem', color: '#374151', lineHeight: '1.5' }}>{submission.submission_text}</p>
        </div>
      )}

      {submission.files && submission.files.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
          <p style={{ margin: 0, fontSize: '0.72rem', fontWeight: 700, color: C.navy }}>Files ({submission.files.length}):</p>
          {submission.files.map((file, idx) => (
            <a
              key={idx}
              href={file.url}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: '9px',
                background: C.bg, padding: '8px 10px', borderRadius: '8px',
                border: `1px solid ${C.border}`, textDecoration: 'none',
              }}
            >
              <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: 'rgba(14,34,93,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="pi pi-file" style={{ fontSize: '0.75rem', color: C.navy }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: 600, color: C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.file_name}</p>
                <p style={{ margin: 0, fontSize: '0.7rem', color: C.sub }}>{formatFileSize(file.size)}</p>
              </div>
              <i className="pi pi-download" style={{ fontSize: '0.78rem', color: C.gold }} />
            </a>
          ))}
        </div>
      )}

      {submission.status === 'graded' && (
        <div style={{ background: '#FFFBEB', padding: '12px', borderRadius: '10px', border: `1.5px solid ${C.gold}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: submission.feedback ? '8px' : 0 }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: C.navy }}>Score</span>
            <span style={{ fontSize: '0.95rem', fontWeight: 800, color: C.navy }}>
              {submission.marks_obtained}/{submission.total_marks}
            </span>
          </div>
          {submission.feedback && (
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#374151', lineHeight: '1.4' }}>{submission.feedback}</p>
          )}
          {submission.reviewed_by_name && (
            <p style={{ margin: '6px 0 0 0', fontSize: '0.72rem', color: C.sub }}>
              <i className="pi pi-user" style={{ fontSize: '0.68rem', marginRight: '4px' }} />
              Reviewed by {submission.reviewed_by_name}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default GradableSubmissionCard;
